import { useState } from "react";
import { Button, Typography } from "antd";
import {
  RUNNING_COMMIT,
  RUNNING_VERSION,
  useVersionPoll,
} from "@/hooks/use_version_poll";
import { useVersionUiStore } from "@/state/version_state";
import WhatsNewDialog from "./whats_new_dialog";

const { Text } = Typography;

/** Short, human-friendly label for the running bundle. */
function runningLabel(): string {
  if (RUNNING_VERSION && RUNNING_VERSION !== "dev") return `v${RUNNING_VERSION}`;
  if (RUNNING_COMMIT && RUNNING_COMMIT !== "dev") return RUNNING_COMMIT.slice(0, 7);
  return "dev";
}

/**
 * In-sider version indicator (collapsed-aware, modelled on tenant-switcher.tsx).
 * Shows the RUNNING bundle's version (baked-in constant, not the polled remote,
 * so it stays correct until the user actually reloads). Clicking opens the
 * optional "Was ist neu" dialog.
 *
 * The dialog also opens once automatically after the user has landed on a new
 * bundle that carries highlights — derived purely (no effect/setState-in-effect),
 * per the app's "render-adjust over effect-sync" convention: `open` is the OR of
 * the auto-open condition and a manual toggle, and closing marks the version seen
 * (which flips the auto condition off). Releases without highlights never nag.
 */
export default function VersionBadge({ collapsed }: { collapsed: boolean }) {
  const { manifest, isCurrent } = useVersionPoll();
  const { seenWhatsNewCommit, markWhatsNewSeen } = useVersionUiStore();
  const [manuallyOpen, setManuallyOpen] = useState(false);

  const remoteCommit = manifest?.commit ?? null;
  const hasEntries = (manifest?.entries?.length ?? 0) > 0;

  const autoOpen =
    isCurrent &&
    hasEntries &&
    !!remoteCommit &&
    remoteCommit !== "dev" &&
    seenWhatsNewCommit !== remoteCommit;

  const open = autoOpen || manuallyOpen;

  function handleClose() {
    setManuallyOpen(false);
    if (remoteCommit) markWhatsNewSeen(remoteCommit);
  }

  const label = runningLabel();

  return (
    <>
      <div style={{ textAlign: "center", marginTop: 4 }}>
        <Button
          type="text"
          size="small"
          onClick={() => setManuallyOpen(true)}
          style={{ padding: collapsed ? 0 : undefined, height: "auto" }}
        >
          <Text type="secondary" style={{ fontSize: collapsed ? 9 : 11 }}>
            {collapsed ? label : `Version ${label}`}
          </Text>
        </Button>
      </div>
      <WhatsNewDialog open={open} onClose={handleClose} manifest={manifest} />
    </>
  );
}
